import { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import type { AlertColor } from '@mui/material/Alert';

/**
 * Hệ thống thông báo (snackbar) toàn cục, độc lập với React tree để axios
 * interceptor (không phải component) vẫn gọi được `notify(...)`.
 * Hỗ trợ queue: nhiều thông báo hiển thị lần lượt, không bị ghi đè.
 */

interface NotifyPayload {
  id: string;
  message: string;
  severity: AlertColor;
}

type Listener = (payload: NotifyPayload) => void;

const listeners = new Set<Listener>();
let notificationCounter = 0;

/** Phát một thông báo tới <Notifier /> đang mount. */
export function notify(message: string, severity: AlertColor = 'info'): void {
  const id = `notification-${++notificationCounter}`;
  listeners.forEach((l) => l({ id, message, severity }));
}

/** Component đặt 1 lần ở gốc app để hiển thị snackbar queue. */
export function Notifier(): JSX.Element {
  const [queue, setQueue] = useState<NotifyPayload[]>([]);
  const [current, setCurrent] = useState<NotifyPayload | null>(null);

  useEffect(() => {
    const listener: Listener = (payload) => {
      setQueue((prev) => [...prev, payload]);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    // Nếu không có thông báo hiện tại và còn trong queue, lấy cái tiếp theo
    if (!current && queue.length > 0) {
      const next = queue[0];
      setCurrent(next);
      setQueue((prev) => prev.slice(1));
    }
  }, [current, queue]);

  const handleClose = () => {
    setCurrent(null);
  };

  return (
    <>
      {current && (
        <Snackbar
          key={current.id}
          open={true}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}

      {queue.length > 0 && (
        <Stack
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1399,
          }}
        >
          <Alert severity="info" variant="outlined">
            {queue.length} thông báo đang chờ
          </Alert>
        </Stack>
      )}
    </>
  );
}
